import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, Eye, EyeOff, Flame, Trophy, ArrowRight, LogOut,
  Star, Brain, Shield, BookOpen, Settings, Target,
  Sparkles, ChevronDown, CheckCircle2, Clock, TrendingUp,
  Award, ShieldCheck, Lock,
  Lightbulb, RefreshCw, HelpCircle, Plus, Minus
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { prefetchStudentView, prefetchParentView } from "@/lib/prefetch";
import NovaLogo from "@/components/NovaLogo";
import NovaOrb from "@/components/NovaOrb";
import AvatarImage from "@/components/AvatarImage";
import SyncStatusIndicator from "@/components/SyncStatusIndicator";
import { useSync } from "@/contexts/SyncContext";

type AuthMode = "login" | "register" | "child";

function getStreakCopy(streak: number): string {
  if (streak >= 7) return `Day ${streak} streak — legendary!`;
  if (streak >= 5) return `Day ${streak} streak — keep it going!`;
  if (streak >= 3) return `Day ${streak} streak — you're on fire!`;
  if (streak === 2) return `Day 2 streak — building momentum!`;
  if (streak === 1) return `Nice start yesterday — keep it going!`;
  return `Ready to start a new streak?`;
}

function getNovaStageLabel(stage: number): string {
  const labels: Record<number, string> = { 1: "Spark", 2: "Glow", 3: "Blaze", 4: "Nova", 5: "Supernova" };
  return labels[stage] || "Spark";
}

function ChildAvatarDisplay({ childId }: { childId: string }) {
  const { data: loadout } = useQuery({
    queryKey: ["/api/children", childId, "avatar-loadout"],
    queryFn: async () => {
      const res = await fetch(`/api/children/${childId}/avatar-loadout`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 60000,
  });
  return <AvatarImage customization={loadout?.characterCustomization || null} size="sm" showFrame={false} />;
}

export default function Dashboard() {
  const [, setLocation] = useLocation();
  const syncState = useSync();
  const [seeding, setSeeding] = useState(false);
  const [ready, setReady] = useState(false);
  const [parentId, setParentId] = useState<string | null>(null);
  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [children, setChildren] = useState<any[]>([]);

  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [childLoginEmail, setChildLoginEmail] = useState("");
  const [childLoginName, setChildLoginName] = useState("");
  const [childLoginPin, setChildLoginPin] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");

  const authRef = useRef<HTMLDivElement>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        setParentId(data.id);
        setParentName(data.name || "");
        setParentEmail(data.email || "");
        setIsAdmin(data.isAdmin || false);
        setChildren(data.children || []);
        setReady(true);
        if (!data.isAdmin) {
          prefetchStudentView();
          prefetchParentView();
        }
      })
      .catch(() => setReady(true));
  }, []);

  const handleLogout = async () => {
    try { await apiRequest("POST", "/api/auth/logout", {}); } catch (e) {}
    window.dispatchEvent(new Event("nova:logout"));
    queryClient.clear();
    setParentId(null); setParentName(""); setParentEmail(""); setIsAdmin(false);
    setChildren([]); setEmail(""); setPassword(""); setConfirmPassword("");
    setName(""); setAuthError("");
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError("");
    if (!name.trim()) { setAuthError("Please enter your name"); return; }
    if (!email.trim()) { setAuthError("Please enter your email"); return; }
    if (password.length < 6) { setAuthError("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { setAuthError("Passwords do not match"); return; }
    setAuthLoading(true);
    try {
      const res = await apiRequest("POST", "/api/auth/register", { name: name.trim(), email: email.trim().toLowerCase(), password, accessCode: accessCode.trim() });
      const data = await res.json();
      if (data.id) {
        setParentId(data.id); setParentName(data.name || name.trim()); setParentEmail(data.email || email.trim().toLowerCase()); setChildren([]);
        window.dispatchEvent(new Event("nova:auth"));
        setLocation("/parent");
      }
    } catch (e: any) {
      const msg = e?.message || "Registration failed";
      setAuthError(msg.includes("409") || msg.toLowerCase().includes("already") ? "This email is already registered. Try logging in instead." : msg.includes("403") || msg.toLowerCase().includes("access code") ? "Invalid access code. Please check and try again." : msg);
    }
    setAuthLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotMessage("");
    if (!forgotEmail.trim()) { setForgotMessage("Please enter your email address"); return; }
    setForgotLoading(true);
    try {
      const res = await apiRequest("POST", "/api/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
      const data = await res.json();
      setForgotMessage(data.message || "If that email is registered, a reset link has been sent.");
    } catch {
      setForgotMessage("If that email is registered, a reset link has been sent.");
    }
    setForgotLoading(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError("");
    if (!email.trim() || !password) { setAuthError("Please enter both email and password"); return; }
    setAuthLoading(true);
    try {
      const res = await apiRequest("POST", "/api/auth/login", { email: email.trim().toLowerCase(), password });
      const data = await res.json();
      if (data.id) {
        setParentId(data.id);
        window.dispatchEvent(new Event("nova:auth"));
        const meRes = await fetch("/api/auth/me", { credentials: "include" });
        const meData = await meRes.json();
        setParentName(meData.name || ""); setParentEmail(meData.email || "");
        const adminFlag = meData.isAdmin || false;
        setIsAdmin(adminFlag);
        const kids = meData.children || [];
        setChildren(kids);
        if (adminFlag) setLocation("/admin");
        else if (kids.length === 0) setLocation("/parent");
      }
    } catch (e: any) {
      const msg = e?.message || "";
      if (msg.includes("401") || msg.toLowerCase().includes("invalid") || msg.toLowerCase().includes("password")) {
        setAuthError("Invalid email or password");
      } else if (msg.includes("429")) {
        setAuthError("Too many login attempts. Please wait a moment and try again.");
      } else if (msg.includes("fetch") || msg.includes("network") || msg.includes("Failed")) {
        setAuthError("Unable to connect. Please check your internet connection.");
      } else {
        setAuthError("Something went wrong. Please try again.");
      }
    }
    setAuthLoading(false);
  };

  const handleChildLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError("");
    if (!childLoginEmail.trim()) { setAuthError("Please enter your parent's email"); return; }
    if (!childLoginName.trim()) { setAuthError("Please enter your name"); return; }
    setAuthLoading(true);
    try {
      const res = await apiRequest("POST", "/api/auth/child-direct-login", {
        parentEmail: childLoginEmail.trim().toLowerCase(),
        childName: childLoginName.trim(),
        pin: childLoginPin || undefined,
      });
      const data = await res.json();
      if (data.child) {
        window.dispatchEvent(new Event("nova:auth"));
        setLocation(`/student?childId=${data.child.id}`);
      }
    } catch (e: any) {
      const msg = e?.message || "";
      if (msg.includes("401")) {
        setAuthError("We couldn't find that account. Check the email, name and PIN.");
      } else if (msg.includes("429")) {
        setAuthError("Too many attempts. Please wait a moment and try again.");
      } else {
        setAuthError("Something went wrong. Please try again.");
      }
    }
    setAuthLoading(false);
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await apiRequest("POST", "/api/seed", {});
      const loginRes = await apiRequest("POST", "/api/auth/login", { email: "parent@lumina.app", password: "demo123" });
      const loginData = await loginRes.json();
      if (loginData.id) {
        setParentId(loginData.id);
        const meRes = await fetch("/api/auth/me", { credentials: "include" });
        const meData = await meRes.json();
        setParentName(meData.name || ""); setParentEmail(meData.email || "");
        setIsAdmin(meData.isAdmin || false); setChildren(meData.children || []);
      }
    } catch (e) { console.error("Seed failed", e); }
    setSeeding(false);
  };

  const scrollToAuth = () => {
    setTimeout(() => authRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };

  const openAuth = (mode: AuthMode) => { setAuthMode(mode); setAuthError(""); setPassword(""); setConfirmPassword(""); scrollToAuth(); };

  if (!ready) {
    return (
      <div className="min-h-screen bg-[#0f0f2e] flex items-center justify-center">
        <NovaOrb state="thinking" stage={1} size="lg" />
      </div>
    );
  }

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 20, scale: 0.95 }, visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring" as const, stiffness: 300, damping: 24 } } };

  if (parentId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f0f2e] via-[#1a1a3e] to-[#0f0f2e] flex flex-col">
        <header className="w-full px-4 sm:px-6 pt-4 pb-2">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <NovaOrb state="idle" stage={1} size="sm" />
              <span className="text-white/60 text-sm font-medium hidden sm:inline">Zenova</span>
            </div>
            <div className="flex items-center gap-3">
              <SyncStatusIndicator syncState={syncState} />
              <div className="text-right">
                <p className="text-white/80 text-sm font-medium truncate max-w-[120px] sm:max-w-none" data-testid="text-parent-name">{parentName}</p>
                <p className="text-white/40 text-xs truncate max-w-[120px] sm:max-w-none" data-testid="text-parent-email">{parentEmail}</p>
              </div>
              <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 rounded-xl text-white/50 hover:text-white hover:bg-white/10 transition-all text-sm" data-testid="button-logout">
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Log out</span>
              </button>
            </div>
          </div>
        </header>

        <div className="flex-1 flex flex-col items-center justify-center p-4 pb-8">
          <div className="max-w-3xl w-full space-y-8 text-center">
            <motion.div className="space-y-3" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-white" data-testid="text-dashboard-title">Who's learning today?</h1>
              <p className="text-slate-400 text-sm" data-testid="text-dashboard-subtitle">Pick a profile to start a session</p>
            </motion.div>

            <motion.div className="space-y-8" variants={containerVariants} initial="hidden" animate="visible">
              {children.length > 0 && (
                <div className={`grid gap-5 ${children.length === 1 ? 'max-w-sm mx-auto' : children.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' : 'md:grid-cols-3'}`}>
                  {children.map((child: any, index: number) => {
                    const g = child.gamificationState || {};
                    const streak = g.streak || 0;
                    const points = g.points || g.novaPoints || 0;
                    const novaStage = g.novaStage || g.stage || 1;
                    const hasSessions = (g.totalSessions || 0) > 0 || !!(g.lastSubject || child.lastSubject);
                    return (
                      <motion.div key={child.id} variants={itemVariants}>
                        <button type="button" className="cursor-pointer group w-full text-left" onClick={() => setLocation(`/student?childId=${child.id}`)} data-testid={`card-student-${child.name.toLowerCase()}`}>
                          <div className="bg-white/[0.07] backdrop-blur-xl border border-white/10 rounded-3xl p-6 hover:border-[#8B5CF6]/40 hover:bg-white/[0.1] transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-[#8B5CF6]/10">
                            <div className="flex flex-col items-center space-y-4">
                              <div className="relative">
                                <div className="w-20 h-20 rounded-full overflow-hidden border-3 border-white/20 shadow-lg group-hover:border-[#8B5CF6]/40 transition-colors">
                                  <span className="w-full h-full flex items-center justify-center text-3xl bg-gradient-to-br from-purple-100 to-indigo-100" data-testid={`img-avatar-${child.id}`}><ChildAvatarDisplay childId={child.id} /></span>
                                </div>
                                <div className="absolute -bottom-1 -right-1 bg-[#1a1a3e] border border-[#8B5CF6]/30 rounded-full px-2 py-0.5 text-[10px] font-semibold text-[#8B5CF6]" data-testid={`text-nova-stage-${child.id}`}>{getNovaStageLabel(novaStage)}</div>
                              </div>
                              <div className="space-y-1 text-center">
                                <h2 className="text-xl font-display font-bold text-white" data-testid={`text-student-name-${child.id}`}>
                                  {child.name}
                                  {child.parentId !== parentId && (
                                    <span className="ml-2 text-[10px] font-semibold bg-pink-500/20 text-pink-300 border border-pink-500/30 px-1.5 py-0.5 rounded-full align-middle" data-testid={`badge-shared-${child.id}`}>Shared</span>
                                  )}
                                </h2>
                                <p className="text-slate-400 text-xs">Age {child.age} · {child.examBoard || "AQA"}</p>
                              </div>
                              <div className="flex items-center gap-4 text-sm" data-testid={`stats-row-${child.id}`}>
                                <div className="flex items-center gap-1" data-testid={`text-streak-${child.id}`}>
                                  <Flame className={`w-4 h-4 ${streak > 0 ? 'text-orange-400' : 'text-slate-600'}`} />
                                  <span className={`font-semibold ${streak > 0 ? 'text-orange-400' : 'text-slate-600'}`}>{streak}</span>
                                </div>
                                <div className="flex items-center gap-1" data-testid={`text-points-${child.id}`}>
                                  <Trophy className="w-4 h-4 text-yellow-400" />
                                  <span className="font-semibold text-yellow-400">{points}</span>
                                </div>
                              </div>
                              <p className="text-[11px] text-slate-500 italic" data-testid={`text-motivational-${child.id}`}>{getStreakCopy(streak)}</p>
                              <div className="w-full bg-[#8B5CF6] text-white rounded-full h-10 text-sm font-medium shadow-lg shadow-[#8B5CF6]/20 group-hover:shadow-[#8B5CF6]/40 transition-shadow flex items-center justify-center" data-testid={`button-learn-${child.id}`}>
                                {hasSessions ? "Continue Learning" : "Start Learning"}
                                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                              </div>
                            </div>
                          </div>
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {children.length === 0 && (
                <motion.div variants={itemVariants} className="py-8">
                  <div className="bg-white/[0.05] border border-white/10 rounded-3xl p-8 max-w-sm mx-auto text-center space-y-4">
                    <BookOpen className="w-10 h-10 text-[#8B5CF6]/50 mx-auto" />
                    <div>
                      <p className="text-white font-medium">No children yet</p>
                      <p className="text-slate-400 text-sm mt-1">Head to the Parent Dashboard to add your first child profile.</p>
                    </div>
                    <Link href="/parent">
                      <Button className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full" data-testid="button-add-first-child">
                        Go to Parent Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              )}

              <motion.div variants={itemVariants} className="flex items-center justify-center gap-4 pt-4">
                <Link href="/parent">
                  <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-all text-sm" data-testid="card-parent-dashboard">
                    <Settings className="w-4 h-4" /> Parent Dashboard
                  </button>
                </Link>
                {isAdmin && (
                  <Link href="/admin">
                    <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-all text-sm" data-testid="link-admin-dashboard">
                      <Shield className="w-4 h-4" /> Admin
                    </button>
                  </Link>
                )}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  const FEATURES = [
    { icon: Brain, title: "Adaptive AI Tutor", desc: "Nova learns how your child thinks — adjusting difficulty, explanations and pace in real time.", color: "from-violet-500 to-purple-600" },
    { icon: Sparkles, title: "Personalised Questions", desc: "Questions woven with your child's interests — football, Minecraft, space — so learning feels relevant.", color: "from-pink-500 to-rose-600" },
    { icon: Clock, title: "Spaced Review", desc: "Smart scheduling brings back topics right before they're forgotten, building lasting knowledge.", color: "from-amber-500 to-orange-600" },
    { icon: Target, title: "Misconception Detection", desc: "When your child gets something wrong, Nova works out why and teaches the right way to think about it.", color: "from-emerald-500 to-green-600" },
    { icon: TrendingUp, title: "Parent Analytics", desc: "Clear dashboards show strengths, gaps and progress — no guesswork, just insight.", color: "from-blue-500 to-cyan-600" },
    { icon: Star, title: "Gamification", desc: "Points, streaks, badges and a growing companion keep motivation high across every session.", color: "from-yellow-500 to-amber-600" },
  ];

  const EXAMS = [
    { name: "11+ Entrance", subjects: "Maths, English, Verbal Reasoning", ages: "Ages 9-11" },
    { name: "KS2 SATs", subjects: "Maths, English, GPS", ages: "Ages 10-11" },
    { name: "GCSE", subjects: "Maths, English Language & Literature", ages: "Ages 14-16" },
  ];

  const HOW_IT_WORKS = [
    { step: "1", title: "Create a profile", desc: "Add your child with their age, exam target and interests. Takes 60 seconds." },
    { step: "2", title: "Nova starts learning", desc: "The AI tutor assesses your child's level and builds a personalised lesson plan." },
    { step: "3", title: "Practice with purpose", desc: "Adaptive questions, worked examples and real-time feedback — every session counts." },
    { step: "4", title: "Track progress", desc: "Watch mastery grow across topics. Nova handles the teaching, you see the results." },
  ];

  const TRUST_BADGES = [
    { icon: ShieldCheck, label: "Child-Safe Platform", desc: "COPPA & UK GDPR compliant" },
    { icon: Lock, label: "Data Protected", desc: "Encrypted & secure" },
    { icon: Award, label: "Educator Approved", desc: "Content by qualified teachers" },
  ];

  const SCIENCE_METHODS = [
    { icon: Target, title: "Zone of Proximal Development", desc: "Nova keeps questions in your child's 'sweet spot' — challenging enough to grow, but never so hard they get frustrated. This concept, developed by educational psychologist Lev Vygotsky, ensures every question builds genuine understanding.", color: "from-violet-500 to-purple-600" },
    { icon: RefreshCw, title: "Spaced Repetition", desc: "Instead of cramming, Nova schedules reviews at scientifically-optimal intervals — just before your child would forget. This transforms short-term memorisation into lasting knowledge that sticks through exam day and beyond.", color: "from-amber-500 to-orange-600" },
    { icon: Lightbulb, title: "Misconception Detection", desc: "When your child gets an answer wrong, Nova doesn't just say 'try again'. It analyses the mistake to understand the underlying misconception, then reteaches the concept in a different way until the penny drops.", color: "from-emerald-500 to-green-600" },
  ];


  const FAQ_ITEMS = [
    { q: "How does Zenova actually work?", a: "Your child works through interactive lessons with Nova, our AI tutor. Nova assesses their level, identifies gaps, and creates a personalised learning path. Questions adapt in real-time — getting harder as your child improves and providing extra support when they struggle. You get clear progress reports after every session." },
    { q: "What exams does Zenova cover?", a: "Zenova currently covers 11+ entrance exams (Maths, English, Verbal Reasoning, Non-Verbal Reasoning), KS2 SATs (Maths, English, GPS), and GCSE Maths and English. All content is aligned with UK exam boards and reviewed by qualified teachers." },
    { q: "How long before we see results?", a: "Every child progresses at their own pace, so there's no single answer. What we can say is that regular, consistent practice matters more than occasional long sessions — we recommend 3-4 sessions per week of 20-30 minutes each. Nova adjusts difficulty continuously, so your child is always working at a level that challenges them without overwhelming them." },
    { q: "Is my child's data safe?", a: "Absolutely. We're fully COPPA and UK GDPR compliant. All data is encrypted, we never share personal information with third parties, and children's accounts are protected with parental controls. You can delete all data at any time from your parent dashboard." },
    { q: "Can I try it before committing?", a: "Yes! You can try an instant demo right now — no sign-up needed. Just click 'Try the demo' to see exactly how Nova teaches. When you're ready, create a free account to start your child's personalised learning journey." },
    { q: "How is this different from other tutoring platforms?", a: "Most platforms just serve up practice questions. Zenova's AI actually understands how your child thinks — it detects misconceptions, adjusts difficulty in real-time, and even weaves your child's interests into questions to keep them engaged. It's the difference between a worksheet and a personal tutor." },
    { q: "What age group is Zenova suitable for?", a: "Zenova is designed for children aged 7-16, covering Key Stage 2 through GCSE. The AI adapts its language, explanations, and difficulty to suit each child's age and ability level." },
    { q: "How much does Zenova cost?", a: "We offer flexible plans starting from a free tier. Visit our pricing page for full details on what's included at each level. You can cancel anytime with no long-term commitment." },
    { q: "Can multiple children use one account?", a: "Yes! You can add multiple child profiles under a single parent account. Each child gets their own personalised learning path, progress tracking, and Nova companion. You can manage all profiles from your parent dashboard." },
    { q: "What if my child gets stuck or frustrated?", a: "Nova is designed to prevent frustration before it happens. If your child struggles, Nova automatically provides simpler stepping-stone questions, worked examples, and encouraging hints. The AI keeps questions in their 'zone of proximal development' — challenging but achievable." },
  ];

  return (
    <div className="min-h-screen bg-[#0f0f2e] text-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a22]/90 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <NovaOrb state="idle" stage={1} size="sm" />
            <span className="text-white font-display font-bold text-lg tracking-tight">Zenova</span>
          </div>
          <div className="flex items-center gap-1">
            <Link href="/pricing">
              <button className="text-sm text-slate-400 hover:text-white transition-colors px-4 py-2 rounded-lg hover:bg-white/5" data-testid="nav-pricing">Pricing</button>
            </Link>
            <button onClick={() => openAuth("login")} className="text-sm text-slate-400 hover:text-white transition-colors px-4 py-2 rounded-lg hover:bg-white/5" data-testid="nav-login">Log in</button>
            <Button onClick={() => openAuth("register")} className="bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#6D28D9] text-white rounded-full text-sm h-9 px-5 shadow-lg shadow-[#8B5CF6]/25 ml-1" data-testid="nav-register">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-1/4 w-[500px] h-[500px] bg-[#8B5CF6]/[0.08] rounded-full blur-[140px]" />
          <div className="absolute top-32 right-1/4 w-[400px] h-[400px] bg-[#EC4899]/[0.08] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-48 bg-[#6D28D9]/[0.05] rounded-full blur-[80px]" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 text-xs text-[#A78BFA] font-medium mb-8" data-testid="badge-exam-types">
              <Sparkles className="w-3.5 h-3.5" />
              11+ · KS2 SATs · GCSE — built for the UK curriculum
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-[1.1] tracking-tight mb-6" data-testid="text-hero-title">
              The AI tutor that<br />
              <span className="bg-gradient-to-r from-[#A78BFA] via-[#C084FC] to-[#F472B6] bg-clip-text text-transparent">actually knows your child</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed mb-10" data-testid="text-hero-subtitle">
              Zenova adapts to how your child learns, weaves their interests into every question, and builds real understanding — not just exam technique.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button onClick={() => openAuth("register")} className="bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] hover:from-[#7C3AED] hover:to-[#6D28D9] text-white rounded-full h-12 px-8 text-base font-semibold shadow-2xl shadow-[#8B5CF6]/40 hover:shadow-[#8B5CF6]/60 transition-all duration-300 hover:scale-[1.02]" data-testid="button-hero-start">
                Start Free <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <button onClick={handleSeed} disabled={seeding} className="group relative inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-[#EC4899]/30 bg-[#EC4899]/[0.08] hover:bg-[#EC4899]/[0.14] hover:border-[#EC4899]/50 text-white font-medium text-sm transition-all duration-300" data-testid="link-demo-account">
                {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 text-[#EC4899]" />}
                {seeding ? "Loading demo..." : "Try instant demo"}
                {!seeding && <span className="text-slate-500 text-xs font-normal hidden sm:inline">— no signup needed</span>}
              </button>
            </div>
          </motion.div>

          <motion.div className="mt-16 flex items-center justify-center" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.6 }}>
            <NovaLogo size="hero" state="idle" stage={3} />
          </motion.div>
        </div>
        <div className="flex justify-center mt-8">
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            <ChevronDown className="w-6 h-6 text-slate-500" />
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <motion.div className="text-center mb-16" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4" data-testid="text-features-title">Everything your child needs to succeed</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">Built by educators and engineers. Powered by AI that understands how children actually learn.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                className="group relative bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:bg-white/[0.06] hover:border-white/[0.10] transition-all duration-300 overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`card-feature-${i}`}
              >
                <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r ${f.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-5 shadow-lg group-hover:scale-105 transition-transform duration-300`}>
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-white font-semibold text-base mb-2">{f.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4" data-testid="section-science">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-16" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-5 py-2 mb-6">
              <Brain className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-semibold">Evidence-Based Learning</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4" data-testid="text-science-title">The science behind Zenova's AI</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">Our adaptive engine is built on three proven educational principles used by the world's best tutors — now available to every child, powered by AI.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {SCIENCE_METHODS.map((method, i) => (
              <motion.div
                key={method.title}
                className="relative bg-white/[0.04] border border-white/[0.06] rounded-2xl p-7 hover:bg-white/[0.06] hover:border-white/10 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                data-testid={`card-science-${i}`}
              >
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${method.color} flex items-center justify-center mb-6 shadow-lg`}>
                  <method.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-bold text-base mb-3" data-testid={`text-science-title-${i}`}>{method.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{method.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div className="mt-10 text-center" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <p className="text-slate-600 text-sm italic">These aren't buzzwords — they're decades-old, research-backed techniques that top tutors already use. Zenova makes them available 24/7.</p>
          </motion.div>
        </div>
      </section>


      <section className="py-24 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <motion.div className="text-center mb-16" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4" data-testid="text-exams-title">Covering the exams that matter</h2>
            <p className="text-slate-400 text-lg">Full curriculum alignment with UK exam boards. Content reviewed by qualified teachers.</p>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { ...EXAMS[0], color: "from-violet-500 to-purple-600", iconBg: "bg-violet-500/10", iconColor: "text-violet-400" },
              { ...EXAMS[1], color: "from-blue-500 to-cyan-600", iconBg: "bg-blue-500/10", iconColor: "text-blue-400" },
              { ...EXAMS[2], color: "from-pink-500 to-rose-600", iconBg: "bg-pink-500/10", iconColor: "text-pink-400" },
            ].map((exam, i) => (
              <motion.div
                key={exam.name}
                className="bg-white/[0.04] border border-white/[0.06] rounded-2xl p-7 text-center hover:bg-white/[0.07] hover:border-white/10 transition-all duration-300 group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`card-exam-${i}`}
              >
                <div className={`w-14 h-14 rounded-2xl ${exam.iconBg} flex items-center justify-center mx-auto mb-5 group-hover:scale-105 transition-transform duration-300`}>
                  <BookOpen className={`w-7 h-7 ${exam.iconColor}`} />
                </div>
                <h3 className="text-white font-bold text-lg mb-2">{exam.name}</h3>
                <p className="text-slate-400 text-sm mb-4">{exam.subjects}</p>
                <span className={`text-xs font-semibold px-3 py-1.5 rounded-full bg-gradient-to-r ${exam.color} text-white shadow-sm`}>{exam.ages}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-16" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4" data-testid="text-how-title">How it works</h2>
            <p className="text-slate-400 text-lg">From sign-up to results in four simple steps.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-5">
            {HOW_IT_WORKS.map((item, i) => (
              <motion.div
                key={item.step}
                className="flex gap-5 items-start bg-white/[0.03] border border-white/[0.05] rounded-2xl p-5 hover:bg-white/[0.05] hover:border-white/[0.08] transition-all duration-300"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center shrink-0 text-white font-bold text-sm shadow-lg shadow-[#8B5CF6]/25">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1.5">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.015] to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto relative">
          <motion.div className="text-center mb-14" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4" data-testid="text-why-title">Why parents choose Nova</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">Not just another question bank — a tutor that thinks.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              "Adapts to your child's exact level — no too-easy busywork, no overwhelming jumps",
              "Detects and corrects misconceptions, not just marks answers right or wrong",
              "Uses your child's interests to make questions engaging and relatable",
              "Builds real understanding through worked examples and step-by-step explanations",
              "Spaced review means topics stick long-term, not just until the next test",
              "Full parent visibility — see exactly what your child knows and where they need help",
            ].map((point, i) => (
              <motion.div
                key={i}
                className="flex gap-3.5 items-start p-4 rounded-xl bg-white/[0.03] border border-white/[0.05] hover:bg-white/[0.05] hover:border-white/[0.08] transition-all duration-200"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm leading-relaxed">{point}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4" data-testid="section-trust-badges">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-14" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4" data-testid="text-trust-title">Safe, secure & built for children</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">Your child's safety is our top priority. We go beyond industry standards to protect young learners.</p>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-5">
            {TRUST_BADGES.map((badge, i) => (
              <motion.div
                key={i}
                className="bg-emerald-500/[0.04] border border-emerald-500/[0.12] rounded-2xl p-7 text-center hover:bg-emerald-500/[0.07] hover:border-emerald-500/[0.20] transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`trust-badge-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
                  <badge.icon className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-white font-bold text-base mb-1.5">{badge.label}</h3>
                <p className="text-slate-400 text-sm">{badge.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            className="mt-8 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-4 text-sm text-slate-500">
              <Link href="/privacy">
                <button className="hover:text-[#8B5CF6] transition-colors underline underline-offset-4" data-testid="link-trust-privacy">Privacy Policy</button>
              </Link>
              <span>·</span>
              <Link href="/terms">
                <button className="hover:text-[#8B5CF6] transition-colors underline underline-offset-4" data-testid="link-trust-terms">Terms of Service</button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>


      <section className="py-20 px-4" data-testid="section-faq">
        <div className="max-w-3xl mx-auto">
          <motion.div className="text-center mb-14" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 rounded-full px-5 py-2 mb-6">
              <HelpCircle className="w-4 h-4 text-[#8B5CF6]" />
              <span className="text-[#8B5CF6] text-sm font-semibold">Common Questions</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4" data-testid="text-faq-title">Frequently asked questions</h2>
            <p className="text-slate-400 text-lg">Everything parents ask before getting started.</p>
          </motion.div>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <motion.div
                key={i}
                className="bg-white/[0.04] border border-white/[0.06] rounded-2xl overflow-hidden"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-colors"
                  id={`button-faq-${i}`}
                  aria-expanded={openFaqIndex === i}
                  aria-controls={`faq-answer-${i}`}
                  data-testid={`button-faq-${i}`}
                >
                  <span className="text-white font-medium text-sm pr-4">{item.q}</span>
                  {openFaqIndex === i ? (
                    <Minus className="w-5 h-5 text-[#8B5CF6] shrink-0" />
                  ) : (
                    <Plus className="w-5 h-5 text-slate-500 shrink-0" />
                  )}
                </button>
                <AnimatePresence>
                  {openFaqIndex === i && (
                    <motion.div
                      id={`faq-answer-${i}`}
                      role="region"
                      aria-labelledby={`button-faq-${i}`}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="px-5 pb-5 overflow-hidden"
                    >
                      <p className="text-slate-400 text-sm leading-relaxed" data-testid={`text-faq-answer-${i}`}>{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section ref={authRef} className="py-24 px-4 bg-gradient-to-b from-transparent to-[#0a0a22]" id="auth-section">
        <div className="max-w-md mx-auto">
          <motion.div className="text-center mb-10" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3" data-testid="text-cta-title">Ready to get started?</h2>
            <p className="text-slate-400">Create a free account and set up your first child in under a minute.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="flex rounded-full bg-white/10 p-1 max-w-sm mx-auto mb-6">
              <button onClick={() => { setAuthMode("login"); setAuthError(""); }} className={`flex-1 rounded-full py-2.5 text-sm font-medium transition-all ${authMode === "login" ? "bg-[#8B5CF6] text-white shadow-lg shadow-[#8B5CF6]/30" : "text-slate-400 hover:text-white"}`} data-testid="tab-login">Parent</button>
              <button onClick={() => { setAuthMode("child"); setAuthError(""); }} className={`flex-1 rounded-full py-2.5 text-sm font-medium transition-all ${authMode === "child" ? "bg-[#EC4899] text-white shadow-lg shadow-[#EC4899]/30" : "text-slate-400 hover:text-white"}`} data-testid="tab-child-login">Student</button>
              <button onClick={() => { setAuthMode("register"); setAuthError(""); }} className={`flex-1 rounded-full py-2.5 text-sm font-medium transition-all ${authMode === "register" ? "bg-[#8B5CF6] text-white shadow-lg shadow-[#8B5CF6]/30" : "text-slate-400 hover:text-white"}`} data-testid="tab-register">Register</button>
            </div>

            <div className="bg-white/[0.07] backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl">
              {authMode === "register" ? (
                <form onSubmit={handleRegister} className="space-y-4 text-left">
                  <div className="space-y-1.5">
                    <Label htmlFor="register-access-code" className="text-slate-300 text-sm">Access Code</Label>
                    <Input id="register-access-code" type="text" placeholder="Enter your access code" value={accessCode} onChange={(e) => setAccessCode(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl font-mono tracking-widest uppercase" data-testid="input-register-access-code" autoComplete="off" />
                    <p className="text-xs text-slate-500">Enter your access code if you have one</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="register-name" className="text-slate-300 text-sm">Full Name</Label>
                    <Input id="register-name" type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl" data-testid="input-register-name" autoComplete="name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="register-email" className="text-slate-300 text-sm">Email</Label>
                    <Input id="register-email" type="email" placeholder="parent@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl" data-testid="input-register-email" autoComplete="email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="register-password" className="text-slate-300 text-sm">Password</Label>
                    <div className="relative">
                      <Input id="register-password" type={showPassword ? "text" : "password"} placeholder="At least 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl pr-10" data-testid="input-register-password" autoComplete="new-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" data-testid="button-toggle-password">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="register-confirm" className="text-slate-300 text-sm">Confirm Password</Label>
                    <Input id="register-confirm" type={showPassword ? "text" : "password"} placeholder="Re-enter your password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl" data-testid="input-register-confirm" autoComplete="new-password" />
                  </div>
                  {authError && <p className="text-sm text-red-400 text-center" data-testid="text-auth-error">{authError}</p>}
                  <Button type="submit" disabled={authLoading} className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full h-12 text-base font-medium shadow-lg shadow-[#8B5CF6]/30" data-testid="button-register">
                    {authLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    {authLoading ? "Creating account..." : "Create Account"}
                  </Button>
                  <p className="text-xs text-slate-500 text-center" data-testid="text-register-legal">
                    By creating an account, you agree to our{" "}
                    <Link href="/terms"><button type="button" className="text-[#8B5CF6] hover:underline" data-testid="link-register-terms">Terms of Service</button></Link>
                    {" "}and{" "}
                    <Link href="/privacy"><button type="button" className="text-[#8B5CF6] hover:underline" data-testid="link-register-privacy">Privacy Policy</button></Link>.
                  </p>
                </form>
              ) : authMode === "child" ? (
                <form onSubmit={handleChildLogin} className="space-y-4 text-left">
                  <div className="text-center mb-2">
                    <p className="text-sm text-slate-400">Log in to your learning space</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="child-email" className="text-slate-300 text-sm">Parent's Email</Label>
                    <Input id="child-email" type="email" placeholder="parent@example.com" value={childLoginEmail} onChange={(e) => setChildLoginEmail(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl" data-testid="input-child-email" autoComplete="email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="child-name" className="text-slate-300 text-sm">Your Name</Label>
                    <Input id="child-name" type="text" placeholder="e.g. Leo" value={childLoginName} onChange={(e) => setChildLoginName(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl" data-testid="input-child-name" autoComplete="off" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="child-pin" className="text-slate-300 text-sm">PIN</Label>
                    <Input id="child-pin" type="password" inputMode="numeric" maxLength={6} placeholder="Enter your PIN" value={childLoginPin} onChange={(e) => setChildLoginPin(e.target.value.replace(/\D/g, ""))} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl text-center tracking-[0.5em] font-mono text-lg" data-testid="input-child-pin" autoComplete="off" />
                  </div>
                  {authError && <p className="text-sm text-red-400 text-center" data-testid="text-auth-error">{authError}</p>}
                  <Button type="submit" disabled={authLoading} className="w-full bg-[#EC4899] hover:bg-[#DB2777] text-white rounded-full h-12 text-base font-medium shadow-lg shadow-[#EC4899]/30" data-testid="button-child-login">
                    {authLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    {authLoading ? "Logging in..." : "Start Learning"}
                  </Button>
                </form>
              ) : showForgotPassword ? (
                <form onSubmit={handleForgotPassword} className="space-y-4 text-left">
                  <div className="space-y-1.5">
                    <Label htmlFor="forgot-email" className="text-slate-300 text-sm">Email address</Label>
                    <Input id="forgot-email" type="email" placeholder="parent@example.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl" data-testid="input-forgot-email" autoComplete="email" />
                  </div>
                  {forgotMessage && <p className="text-sm text-emerald-400 text-center" data-testid="text-forgot-message">{forgotMessage}</p>}
                  <Button type="submit" disabled={forgotLoading} className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full h-12 text-base font-medium shadow-lg shadow-[#8B5CF6]/30" data-testid="button-forgot-submit">
                    {forgotLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    {forgotLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                  <button type="button" onClick={() => { setShowForgotPassword(false); setForgotMessage(""); }} className="w-full text-sm text-slate-400 hover:text-white transition-colors" data-testid="button-back-to-login">
                    Back to login
                  </button>
                </form>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4 text-left">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email" className="text-slate-300 text-sm">Email</Label>
                    <Input id="login-email" type="email" placeholder="parent@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl" data-testid="input-login-email" autoComplete="email" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password" className="text-slate-300 text-sm">Password</Label>
                      <button type="button" onClick={() => { setShowForgotPassword(true); setForgotEmail(email); setAuthError(""); }} className="text-xs text-purple-400 hover:text-purple-300 transition-colors" data-testid="link-forgot-password">Forgot password?</button>
                    </div>
                    <div className="relative">
                      <Input id="login-password" type={showPassword ? "text" : "password"} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-white/10 border-white/10 text-white placeholder:text-slate-500 h-11 rounded-xl pr-10" data-testid="input-login-password" autoComplete="current-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" data-testid="button-toggle-password-login">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {authError && <p className="text-sm text-red-400 text-center" data-testid="text-auth-error">{authError}</p>}
                  <Button type="submit" disabled={authLoading} className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full h-12 text-base font-medium shadow-lg shadow-[#8B5CF6]/30" data-testid="button-login">
                    {authLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    {authLoading ? "Logging in..." : "Log In"}
                  </Button>
                </form>
              )}
            </div>

            <div className="text-center mt-4">
              <button onClick={handleSeed} disabled={seeding} className="text-sm text-slate-500 hover:text-[#8B5CF6] underline underline-offset-4 transition-colors" data-testid="link-demo-account-bottom">
                {seeding ? "Setting up demo..." : "Try with demo account"}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-white/[0.06] py-10 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <NovaOrb state="idle" stage={1} size="sm" />
            <span className="text-white/50 text-sm font-medium">Zenova</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-white/30">
            <Link href="/pricing">
              <button className="hover:text-white/60 transition-colors" data-testid="link-footer-pricing">Pricing</button>
            </Link>
            <Link href="/privacy">
              <button className="hover:text-white/60 transition-colors" data-testid="link-footer-privacy">Privacy Policy</button>
            </Link>
            <Link href="/terms">
              <button className="hover:text-white/60 transition-colors" data-testid="link-footer-terms">Terms of Service</button>
            </Link>
          </div>
          <p className="text-white/20 text-xs">Adaptive AI tutoring for every child.</p>
        </div>
      </footer>
    </div>
  );
}
